import { useEffect, useState } from "react";
import { useFeedbackAlerts } from "../../_app/hooks";
import { useUserRegister } from "../hooks";
import { genericError } from "../../_app/utils/text";
import { createStylesheet } from "../../_app/utils/styles";
import { UIAlert, UIAlertTitle, UIButton, UILink, UITextField } from "../../_app/components";
import AuthLayout from "../../auth/layouts/AuthLayout";

const Register = () => {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const classes = useStyles();
  const [email, setEmail] = useState("");
  const [accountCode, setAccountCode] = useState("");
  const [emailExists, setEmailExists] = useState(false);
  const [notMatch, setNotMatch] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const { mutate: executeRegister, error, isSuccess, isError } = useUserRegister();

  const handleRegister = async () => {
    setNotMatch(false);
    setEmailExists(false);
    setConfirmed(false);
    executeRegister({ accountCode, email });
  };

  useEffect(() => {
    if (isSuccess) {
      setNotMatch(false);
      setEmailExists(false);
      setConfirmed(true);
    } else if (isError) {
      switch ((error as any)?.status) {
        case 404: {
          setNotMatch(true);
          setEmailExists(false);
          setConfirmed(false);
          break;
        }
        case 409: {
          setNotMatch(false);
          setEmailExists(true);
          setConfirmed(false);
          break;
        }
        default: {
          setFeedbackAlertError((error as any)?.statusText || (error as any)?.data?.message || genericError());
        }
      }
    }
  }, [error, isSuccess, isError, setFeedbackAlertError]);

  return (
    <AuthLayout title="Register" data-cy="register-page">
      <div className={classes.contentWrap}>
        {!notMatch && !emailExists && !confirmed && (
          <UIAlert severity="info" className={classes.alert}>
            Please enter your account number and the email address that receives the monthly bill. For security reasons, we can
            only register this email, however once registered, you can create additional users.
          </UIAlert>
        )}
        {notMatch && (
          <UIAlert severity="error" className={classes.alert}>
            Your account number and/or email address do not match our records. Please check the details and retry. If the problem
            persists, please contact customer support.
          </UIAlert>
        )}
        {emailExists && (
          <UIAlert severity="error" className={classes.alert}>
            A user already exists for this email address. Please try to Reset your password.
          </UIAlert>
        )}
        {confirmed && (
          <UIAlert severity="success" className={classes.alert}>
            <UIAlertTitle className={classes.successTitle}>Thank you for registration</UIAlertTitle>
            An email has been sent. Please check your spam folder if you do not find the email in your inbox. Click{" "}
            <UILink href="/">here</UILink> to go to login page.
          </UIAlert>
        )}
        {!confirmed && (
          <>
            <UITextField
              id="account"
              name="account"
              label="Account Number"
              value={accountCode || ""}
              variant="outlined"
              size="small"
              fullWidth
              onChange={(e) => setAccountCode(e.target.value)}
            />
            <UITextField
              id="email"
              name="email"
              label="Email Address"
              type="email"
              value={email || ""}
              variant="outlined"
              size="small"
              fullWidth
              onChange={(e) => setEmail(e.target.value)}
            />
            <UIButton
              className={classes.register}
              variant="contained"
              color="primary"
              onClick={handleRegister}
              disabled={Boolean(!accountCode || !email)}
            >
              Register
            </UIButton>
          </>
        )}
      </div>
    </AuthLayout>
  );
};

const useStyles = createStylesheet((theme) => {
  return {
    contentWrap: {
      flex: 1,
      flexDirection: "column",
      gap: "25px",
      padding: "25px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    alert: {
      alignItems: "center",
    },
    successTitle: {
      fontWeight: "bold",
      marginBottom: "5px",
    },
    register: {
      width: "100%",
    },
  };
});

export default Register;
